/**
 * Settings → Notifications (SPEC `notifications` § Settings tab).
 *
 * Saves like General and for the same reason: a local draft and one partial `PUT /api/settings` of
 * the `notifications` blob on Save. Nothing is armed mid-edit, so a half-typed floor ("1" on the
 * way to "150") can never be the threshold a scrape is judged against.
 *
 * The tab owns its own draft and Save button rather than sharing General's — the two blobs are
 * independent, and a Save here must not resend scheduling keys the user did not touch.
 */

import { useMemo, useState } from 'react';
import {
  VIRAL_RULE_METRICS,
  type NotificationsSettings,
  type SettingsResponse,
  type ViralRuleMetric,
} from '@lw/shared';
import { describeError, isApiError } from '../../api/client';
import { Spinner } from '../../components';
import { AGGREGATE_METRIC_LABEL } from '../../lib/labels';
import { useUpdateSettings } from './useSettings';

type RuleDraft = { enabled: boolean; multiplier: string; floor: string };
type Draft = { rules: Record<ViralRuleMetric, RuleDraft>; refireGrowthPct: string };

function toDraft(settings: NotificationsSettings): Draft {
  const rules = {} as Record<ViralRuleMetric, RuleDraft>;
  for (const metric of VIRAL_RULE_METRICS) {
    const rule = settings.rules[metric];
    rules[metric] = {
      enabled: rule.enabled,
      multiplier: String(rule.multiplier),
      floor: String(rule.floor),
    };
  }
  return { rules, refireGrowthPct: String(settings.refireGrowthPct) };
}

/** `null` when the draft is not a legal body — Save stays disabled and the field says why. */
function toBody(draft: Draft): NotificationsSettings | null {
  const rules = {} as NotificationsSettings['rules'];
  for (const metric of VIRAL_RULE_METRICS) {
    const rule = draft.rules[metric];
    const multiplier = Number(rule.multiplier);
    const floor = Number(rule.floor);
    if (rule.multiplier.trim().length === 0 || !Number.isFinite(multiplier) || multiplier < 1) {
      return null;
    }
    if (rule.floor.trim().length === 0 || !Number.isInteger(floor) || floor < 0) return null;
    rules[metric] = { enabled: rule.enabled, multiplier, floor };
  }
  const growth = Number(draft.refireGrowthPct);
  if (!Number.isInteger(growth) || growth < 1 || growth > 1000) return null;
  return { rules, refireGrowthPct: growth };
}

/** Server `details` are `"notifications.rules.reactions.floor: …"` strings — keyed back per field. */
function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error) || !Array.isArray(error.details)) return {};
  const errors: Record<string, string> = {};
  for (const entry of error.details) {
    if (typeof entry !== 'string') continue;
    const separator = entry.indexOf(': ');
    if (separator === -1) continue;
    errors[entry.slice(0, separator)] = entry.slice(separator + 2);
  }
  return errors;
}

export type NotificationsSectionProps = { settings: SettingsResponse };

export function NotificationsSection({ settings }: NotificationsSectionProps) {
  const update = useUpdateSettings();
  // Seeded once, on mount — the section unmounts with its tab, so re-opening re-seeds naturally.
  // A refetch-on-focus must never overwrite a half-typed threshold (the General tab does the same).
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings.notifications));
  const [saved, setSaved] = useState(false);

  const body = useMemo(() => toBody(draft), [draft]);
  const errors = update.isError ? fieldErrors(update.error) : {};

  const patchRule = (metric: ViralRuleMetric, changes: Partial<RuleDraft>) => {
    setSaved(false);
    setDraft((current) => ({
      ...current,
      rules: { ...current.rules, [metric]: { ...current.rules[metric], ...changes } },
    }));
  };

  const save = () => {
    if (body === null) return;
    update.mutate(
      { notifications: body },
      {
        onSuccess: (next) => {
          setDraft(toDraft(next.notifications));
          setSaved(true);
        },
      },
    );
  };

  return (
    <section className="settings-section">
      <h3 className="settings-title">Viral alerts</h3>
      <p className="field__hint">
        A rule fires when the post's metric is at least N× the author's median <strong>and</strong>{' '}
        at least the floor. Posts without a baseline — fewer than 5 counted posts, or a median of 0
        — never fire.
      </p>

      {update.isError && Object.keys(errors).length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(update.error)}
        </p>
      ) : null}

      <div className="notif-rules">
        {VIRAL_RULE_METRICS.map((metric) => {
          const rule = draft.rules[metric];
          const multiplierError = errors[`notifications.rules.${metric}.multiplier`];
          const floorError = errors[`notifications.rules.${metric}.floor`];
          return (
            <div className="notif-rule" key={metric}>
              <label className="toggle notif-rule__on">
                <input
                  type="checkbox"
                  checked={rule.enabled}
                  onChange={(event) => patchRule(metric, { enabled: event.target.checked })}
                />
                <span>{AGGREGATE_METRIC_LABEL[metric]}</span>
              </label>

              <div className="field notif-rule__field">
                <label className="field__label" htmlFor={`notif-mult-${metric}`}>
                  × median
                </label>
                <input
                  id={`notif-mult-${metric}`}
                  className="input"
                  type="number"
                  min={1}
                  step="0.1"
                  value={rule.multiplier}
                  onChange={(event) => patchRule(metric, { multiplier: event.target.value })}
                />
                {multiplierError === undefined ? null : (
                  <span className="settings-bad">{multiplierError}</span>
                )}
              </div>

              <div className="field notif-rule__field">
                <label className="field__label" htmlFor={`notif-floor-${metric}`}>
                  at least
                </label>
                <input
                  id={`notif-floor-${metric}`}
                  className="input"
                  type="number"
                  min={0}
                  step="1"
                  value={rule.floor}
                  onChange={(event) => patchRule(metric, { floor: event.target.value })}
                />
                {floorError === undefined ? null : (
                  <span className="settings-bad">{floorError}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="field notif-refire">
        <label className="field__label" htmlFor="notif-refire">
          Alert again when a metric grows this much past its last alert (%)
        </label>
        <input
          id="notif-refire"
          className="input"
          type="number"
          min={1}
          max={1000}
          step="1"
          value={draft.refireGrowthPct}
          onChange={(event) => {
            setSaved(false);
            setDraft((current) => ({ ...current, refireGrowthPct: event.target.value }));
          }}
        />
        {errors['notifications.refireGrowthPct'] === undefined ? (
          <span className="field__hint">
            Growth below this is not news — no cooldown, no repeated card for the same numbers.
          </span>
        ) : (
          <span className="settings-bad">{errors['notifications.refireGrowthPct']}</span>
        )}
      </div>

      <div className="notif-save">
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={body === null || update.isPending}
        >
          {update.isPending ? <Spinner size="sm" /> : null}
          Save notifications
        </button>
        {saved && !update.isPending ? <span className="muted dialog-hint">Saved.</span> : null}
        {body === null ? (
          <span className="settings-bad">
            Multipliers are at least 1, floors are whole numbers, and the re-fire percent is 1-1000.
          </span>
        ) : null}
      </div>
    </section>
  );
}

export default NotificationsSection;
